import React from 'react';

import { useTranslation } from 'react-i18next';
import { Icon as IconObject } from '../../../core/constants/Icon';
import { BookingStatus } from '../../../core/enums/BookingStatus';
import { Color } from '../../../core/enums/Color';
import { Size } from '../../../core/enums/Size';

import Badge, { BadgeSize } from '../badge/Badge';
import Icon, { IconSize } from '../../../components/icon/Icon';

const statusIcon: Record<BookingStatus, keyof typeof IconObject> = {
  [BookingStatus.Pending]: 'stopwatch',
  [BookingStatus.Open]: 'open',
  [BookingStatus.Done]: 'check',
  [BookingStatus.Canceled]: 'x',
  [BookingStatus.Blocked]: 'x',
  [BookingStatus.Rejected]: 'x'
};

const statusLabel: Record<BookingStatus, string> = {
  [BookingStatus.Pending]: 'booking.status.pending',
  [BookingStatus.Open]: 'booking.status.open',
  [BookingStatus.Done]: 'booking.status.done',
  [BookingStatus.Canceled]: 'booking.status.canceled',
  [BookingStatus.Blocked]: 'booking.status.blocked',
  [BookingStatus.Rejected]: 'booking.status.rejected'
};

const statusColor: Record<BookingStatus, Color> = {
  [BookingStatus.Pending]: Color.Orange,
  [BookingStatus.Open]: Color.Red,
  [BookingStatus.Done]: Color.Green,
  [BookingStatus.Canceled]: Color.Grey,
  [BookingStatus.Blocked]: Color.Violet,
  [BookingStatus.Rejected]: Color.Grey
};

export const iconSize: Record<BadgeSize, IconSize> = {
  [Size.ExtraSmall]: 'extra-small',
  [Size.Small]: 'extra-small',
  [Size.Medium]: 'extra-small',
  [Size.Large]: 'medium'
};

interface Props {
  status: BookingStatus;
  size?: BadgeSize;
  rejectionReason: string;
}

const BookingStatusBadge: React.FC<Props> = ({ status, size = Size.Medium, rejectionReason }) => {
  const { t } = useTranslation();
  const isCanceled = status === BookingStatus.Rejected || status === BookingStatus.Canceled;

  if (!statusLabel[status]) {
    return <></>;
  }

  return (
    <Badge
      color={statusColor[status]}
      icon={!isCanceled && <Icon icon={statusIcon[status]} color={statusColor[status]} size={iconSize[size]} svg />}
      label={t(statusLabel[status])}
      size={size}
      tooltip={isCanceled ? rejectionReason : undefined}
      withTransparentIcon
      monochrome
    />
  );
};

export default BookingStatusBadge;
